import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download } from "lucide-react";

interface ReportResultsTableProps {
  columns: string[];
  data: Array<Record<string, unknown>>;
  sqlQuery?: string | null;
  onSave?: () => void;
  isLoading?: boolean;
}

/**
 * Escapes a value for CSV format
 */
const escapeCSVValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);
  // If value contains comma, newline, or quote, wrap in quotes and escape internal quotes
  if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/**
 * Converts data to CSV format and triggers download
 */
const exportToCSV = (columns: string[], data: Array<Record<string, unknown>>): void => {
  // Create header row
  const headerRow = columns.map(escapeCSVValue).join(",");

  // Create data rows
  const dataRows = data.map((row) =>
    columns.map((column) => escapeCSVValue(row[column])).join(",")
  );

  // Combine header and data with BOM for Excel UTF-8 compatibility
  const csvContent = "\uFEFF" + [headerRow, ...dataRows].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `report_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ReportResultsTable = ({
  columns,
  data,
  sqlQuery,
  onSave,
  isLoading = false,
}: ReportResultsTableProps): JSX.Element => {
  const [showSql, setShowSql] = useState<boolean>(false);

  if (!columns.length) {
    return <></>;
  }

  const handleExportCSV = (): void => {
    exportToCSV(columns, data);
  };

  return (
    <div className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-neutral-900">Report Results</h3>
        <div className="flex items-center gap-2">
          {sqlQuery && (
            <Button variant="outline" size="sm" onClick={() => setShowSql((prev) => !prev)}>
              {showSql ? "Hide SQL" : "View SQL"}
            </Button>
          )}
          {data.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="mr-1.5 h-4 w-4" />
              Export CSV
            </Button>
          )}
          {onSave && (
            <Button size="sm" onClick={onSave} disabled={isLoading}>
              Save Report
            </Button>
          )}
        </div>
      </div>

      {sqlQuery && showSql && (
        <pre className="max-h-72 overflow-auto rounded-md bg-neutral-900 p-3 text-xs text-neutral-100">
          <code>{sqlQuery}</code>
        </pre>
      )}

      <div className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-sm text-neutral-500">
                  Loading results…
                </TableCell>
              </TableRow>
            ) : data.length ? (
              data.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => {
                    const value = row[column];
                    const displayValue = value === null || value === undefined ? "" : String(value);
                    return <TableCell key={`${column}-${index}`}>{displayValue}</TableCell>;
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-sm text-neutral-500">
                  No data returned for this report.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
